import { Slot, Slottable } from '@radix-ui/react-slot';
import {
  Skeleton,
  SkeletonTextLines,
} from '@tuic/web-compositions/core/Skeleton';
import { Tag } from '@tuic/web-compositions/core/Tag';
import { Image } from '@tuic/web-compositions/shared/Image';
import { twJoin } from 'tailwind-merge';

import type { ImageProps } from '@tuic/web-compositions/shared/Image';
import type { FC, PropsWithChildren } from 'react';

type TariffCardProps = PropsWithChildren<{
  fallback?: boolean;
  asChild?: boolean;
  image?: ImageProps;
  headline?: string;
  text?: string;
  priceExpression?: string;
  tag?: string;
}>;

export const TariffCard: FC<TariffCardProps> = ({
  fallback,
  asChild,
  image,
  headline,
  text,
  priceExpression,
  tag,
  children,
}) => {
  const Element = asChild ? Slot : 'div';

  return (
    <Element
      className={twJoin(
        'block appearance-none text-left w-full h-full',
        'group/tariff-card',
        'bg-white rounded-sm overflow-hidden',
        'text-marine-high-emphasis',
        'focus-visible:outline-3 focus-visible:outline focus-visible:outline-ocean-100',
        'interactive:hover:ring-2 interactive:hover:ring-surface-secondary-20',
      )}
    >
      {fallback && <Skeleton className="aspect-16/9 rounded-none" />}
      {!fallback && image && (
        <div className={twJoin('relative overflow-hidden aspect-16/9')}>
          {}
          <Image
            {...image}
            className="
              aspect-16/9
              group-interactive-hover/tariff-card:scale-110 h-full transition
            "
          />
          {tag && (
            <div className="absolute right-4 bottom-4">
              <Tag variant="white">{tag}</Tag>
            </div>
          )}
        </div>
      )}

      {fallback && (
        <div className="px-4 py-5 space-y-4">
          <SkeletonTextLines number={1} />
          <SkeletonTextLines number={2} className="h-1.5" />
        </div>
      )}

      {!fallback && (headline || text || priceExpression) && (
        <div className={twJoin('p-4 space-y-1 h-full')}>
          {(headline || priceExpression) && (
            <div className="flex headline-sm">
              {headline && <div>{headline}</div>}
              {priceExpression && (
                <div className="flex-1 text-right">{priceExpression}</div>
              )}
            </div>
          )}
          {text && <div className="text-lg">{text}</div>}
        </div>
      )}
      <Slottable>{children}</Slottable>
    </Element>
  );
};
